import type { useAuth } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, cn } from "@ndd/ui";
import { CircleAlert, CircleCheck, Info, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState, type ReactElement } from "react";
import type { PatientQuickNoteHealthieSyncResponse } from "../../../../shared/api-types";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import {
  msUntilStalePending,
  quickNoteSyncLine,
  type QuickNoteSyncTone,
} from "@/lib/patient-quick-note-healthie-sync";
import {
  quickNoteHealthieSyncPath,
  quickNoteHealthieSyncQueryKeyFor,
  quickNoteHealthieSyncRetryPath,
  quickNoteSubjectId,
  quickNoteSubjectScope,
  type QuickNoteSubject,
} from "@/lib/patient-quick-note-subject";

type ClerkGetToken = ReturnType<typeof useAuth>["getToken"];
type SyncResponse = { data: PatientQuickNoteHealthieSyncResponse | null };

const SENDING_POLL_MS = 4_000;
const TONE_CLASSES: Record<QuickNoteSyncTone, string> = {
  neutral: "text-muted-foreground",
  success: "text-success",
  danger: "text-danger",
};

/**
 * The Patient Quick Note's copy in Healthie (NO-750), one line under the
 * editor in the SOAP writeback line's shape. The route answers 404 while
 * Healthie sync is off for this RD, and then nothing renders. `savedRevisionId`
 * changes after each autosave, which refreshes the line.
 */
export function PatientQuickNoteHealthieSync(props: {
  subject: QuickNoteSubject;
  savedRevisionId: string | null;
  getToken: ClerkGetToken;
}): ReactElement | null {
  const sync = useQuickNoteHealthieSync(props);

  if (sync.state === undefined) return null;

  return (
    <QuickNoteSyncLineView
      state={sync.state}
      retrying={sync.retrying}
      onRetry={sync.retry}
    />
  );
}

/**
 * The sync line's query key, kept to one array per subject scope and id, so
 * a caller that rebuilds its subject object on every render never re-runs
 * the invalidate effect below.
 */
function useStableSyncQueryKey(subject: QuickNoteSubject): string[] {
  const scope = quickNoteSubjectScope(subject);
  const id = quickNoteSubjectId(subject);

  return useMemo(() => quickNoteHealthieSyncQueryKeyFor(scope, id), [scope, id]);
}

function useQuickNoteHealthieSync({
  subject,
  savedRevisionId,
  getToken,
}: {
  subject: QuickNoteSubject;
  savedRevisionId: string | null;
  getToken: ClerkGetToken;
}) {
  const queryClient = useQueryClient();
  const queryKey = useStableSyncQueryKey(subject);
  const sync = useQuery({
    queryKey,
    queryFn: () =>
      authenticatedApi<SyncResponse>(getToken, quickNoteHealthieSyncPath(subject)),
    retry: (failures, error) => !isNotFound(error) && failures < 2,
    refetchInterval: (query) =>
      sending(query.state.data?.data ?? null) ? SENDING_POLL_MS : false,
    staleTime: 0,
    gcTime: 0,
  });
  const retry = useMutation({
    mutationFn: () =>
      authenticatedApi<SyncResponse>(
        getToken,
        quickNoteHealthieSyncRetryPath(subject),
        {
          method: "POST",
        }
      ),
    onSuccess: (response) => queryClient.setQueryData(queryKey, response),
    onError: captureRetryFailure,
  });

  useEffect(() => {
    if (savedRevisionId) void queryClient.invalidateQueries({ queryKey });
  }, [savedRevisionId, queryKey, queryClient]);

  useRenderWhenPendingTurnsStale(sync.data?.data ?? null);

  return {
    state: sync.data?.data,
    retrying: retry.isPending,
    retry: () => retry.mutate(),
  };
}

/** Re-renders once a pending send crosses the stale threshold, so Retry appears. */
function useRenderWhenPendingTurnsStale(
  state: PatientQuickNoteHealthieSyncResponse | null
): void {
  const [, setTick] = useState(0);
  const delay = msUntilStalePending(state);

  useEffect(() => {
    if (delay === null) return;

    const timer = setTimeout(() => setTick((tick) => tick + 1), delay);

    return () => clearTimeout(timer);
  }, [delay, state?.updatedAt]);
}

function QuickNoteSyncLineView({
  state,
  retrying,
  onRetry,
}: {
  state: PatientQuickNoteHealthieSyncResponse | null;
  retrying: boolean;
  onRetry: () => void;
}): ReactElement {
  const line = quickNoteSyncLine(state);

  return (
    <div
      className="mt-2 flex items-start justify-between gap-3"
      data-healthie-sync-state={state?.status ?? "not_sent"}
    >
      <p role="status" className="flex items-start gap-2 text-body-sm leading-[18px]">
        <SyncIcon tone={line.tone} sending={line.sending} />
        <span>
          <span
            className={cn(
              "font-medium",
              line.tone === "neutral" ? "text-foreground" : TONE_CLASSES[line.tone]
            )}
          >
            {line.headline}
          </span>
          <span className="text-muted-foreground"> {line.detail}</span>
        </span>
      </p>
      {line.retryLabel ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={retrying}
          onClick={onRetry}
        >
          {line.retryLabel}
        </Button>
      ) : null}
    </div>
  );
}

function captureRetryFailure(): void {
  Sentry.captureException(new Error("patient_quick_note_healthie_sync_retry_failed"), {
    tags: { operation: "sync_retry", source: "client", surface: "patient_quick_notes" },
  });
}

function SyncIcon({
  tone,
  sending,
}: {
  tone: QuickNoteSyncTone;
  sending: boolean;
}): ReactElement {
  const className = cn("mt-0.5 size-3.5 shrink-0", TONE_CLASSES[tone]);
  // The spinner keeps spinning under reduced motion: it is status, not decoration.
  if (sending)
    return <Loader2 aria-hidden="true" className={cn(className, "animate-spin")} />;

  if (tone === "success")
    return <CircleCheck aria-hidden="true" className={className} />;

  if (tone === "danger")
    return <CircleAlert aria-hidden="true" className={className} />;

  return <Info aria-hidden="true" className={className} />;
}

function sending(state: PatientQuickNoteHealthieSyncResponse | null): boolean {
  return state?.status === "pending" || state?.status === "running";
}

function isNotFound(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 404;
}
